<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { Check, ChevronDown, Languages } from "lucide-vue-next";
import { locale, setLocale, t, type AppLocale } from "@/i18n";

const root = ref<HTMLElement>();
const trigger = ref<HTMLElement>();
const menu = ref<HTMLElement>();
const open = ref(false);
const menuStyle = ref<Record<string, string>>({});
const options: Array<{ value: AppLocale; code: string; label: string; native: string }> = [
  { value: "de", code: "DE", label: "Deutsch", native: "Deutsch" },
  { value: "en", code: "EN", label: "Englisch", native: "English" }
];
function select(value: AppLocale) {
  setLocale(value);
  open.value = false;
}
function updatePosition() {
  const rect = trigger.value?.getBoundingClientRect();
  if (!rect) return;
  const width = 176;
  const left = Math.min(Math.max(12, rect.right - width), Math.max(12, window.innerWidth - width - 12));
  menuStyle.value = { top: `${rect.bottom + 8}px`, left: `${left}px`, width: `${width}px` };
}
const MENU_EVENT = "moonshot:header-menu";
function closeForOtherMenu(event: Event) { if ((event as CustomEvent).detail !== "language") open.value = false; }
async function toggle() {
  open.value = !open.value;
  if (!open.value) return;
  window.dispatchEvent(new CustomEvent(MENU_EVENT, { detail: "language" }));
  await nextTick();
  updatePosition();
}
function closeOnOutside(event: PointerEvent) {
  if (open.value && event.target instanceof Node && !root.value?.contains(event.target) && !menu.value?.contains(event.target)) open.value = false;
}
function closeOnEscape(event: KeyboardEvent) {
  if (event.key === "Escape") open.value = false;
}
onMounted(() => {
  window.addEventListener("pointerdown", closeOnOutside);
  window.addEventListener("keydown", closeOnEscape);
  window.addEventListener("resize", updatePosition);
  window.addEventListener("scroll", updatePosition, true);
  window.addEventListener(MENU_EVENT, closeForOtherMenu);
});
onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", closeOnOutside);
  window.removeEventListener("keydown", closeOnEscape);
  window.removeEventListener("resize", updatePosition);
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener(MENU_EVENT, closeForOtherMenu);
});
</script>

<template>
  <span ref="root" class="language-select">
    <button ref="trigger" type="button" class="language-select__trigger" :aria-label="t('header.language')" :title="t('header.language')" :aria-expanded="open" @click="toggle">
      <Languages :size="14" aria-hidden="true" />
      <strong>{{ locale.toUpperCase() }}</strong>
      <ChevronDown :size="11" aria-hidden="true" />
    </button>
    <Teleport to="body"><Transition name="hp-pop"><div v-if="open" ref="menu" class="language-select__menu language-select__menu--teleported hp hp-language" :style="menuStyle" role="menu" :aria-label="t('header.language')">
      <span>{{ t('header.language') }}</span>
      <button v-for="option in options" :key="option.value" type="button" role="menuitemradio" :aria-checked="locale === option.value" :title="option.label" @click="select(option.value)">
        <i>{{ option.code }}</i>
        <strong>{{ option.native }}</strong>
        <Check v-if="locale === option.value" :size="14" aria-hidden="true" />
      </button>
    </div></Transition></Teleport>
  </span>
</template>
